import rfStore from "@/utils/rfStore";
import hmacSHA512 from "crypto-js/hmac-sha512";
import Base64 from "crypto-js/enc-base64";
import MD5 from "crypto-js/md5";
import { GlobalState } from "@/interfaces/common";
import Toast from "@/components/Toast";
import { i18n } from "next-i18next";
import JSONBig from "json-bigint";
import log2Sentry from "./log2Sentry";
import { ApiError } from "./sentryError";
import { isInApp } from "./util";

// 有的错误不需要提示
const NOT_NOTIFY_ERROR_MESSAGE_PATH = ["/verification/api/captcha/app/security/anonymous"];
function notifyErrorMessage({ code, message }: any) {
	let errorMsg = i18n?.t("error:mysterious_error", { code }) || "";
	const isExistErrCode = i18n?.exists(`error:${code}`);
	if (isExistErrCode) {
		errorMsg = i18n?.t(`error:${code}`) || "";
	}
	if (errorMsg) {
		Toast.fail(errorMsg);
	}
}

/**
 * 生成摘要
 */
function generateDigest({
	path,
	init,
	token,
	userId,
	headers,
}: {
	path: string;
	init: RequestInit;
	token: string;
	userId: string;
	headers: HeadersInit;
}) {
	// 公共请求头加密需要按照如下顺序
	const headersOrder = ["appId", "appVersion", "deviceId", "language", "osVersion", "platformType", "region"];

	let digestStr = `${init.method?.toUpperCase()}${path}${token}${userId}`;
	headersOrder.map((key, index) => {
		digestStr += headers[key as keyof HeadersInit];
	});
	if (init.body) {
		digestStr += MD5(init.body.toString());
	}

	return Base64.stringify(hmacSHA512(digestStr, token));
}

/**
 * 生成请求头
 * @param path
 * @param init
 * @param clientInfo
 * @returns
 */
function generateHeaders(path: string, init: RequestInit, clientInfo?: GlobalState) {
	if (!clientInfo) {
		return {};
	}

	const headers: HeadersInit = {
		appId: clientInfo.appId,
		appVersion: clientInfo.appVersion,
		deviceId: clientInfo.deviceId,
		platformType: clientInfo.platformType,
		osVersion: clientInfo.osVersion,
		language: clientInfo.language ? clientInfo.language : "en",
	};

	if (clientInfo.region) {
		headers.region = clientInfo.region;
	}

	if (clientInfo.token && clientInfo.userId) {
		// headers.userId = clientInfo.userId;
		headers.Authorization = `Bearer ${clientInfo.token}`;

		headers.Signature = generateDigest({
			path,
			init,
			token: clientInfo.token,
			userId: clientInfo.userId,
			headers: { ...headers },
		});
	}

	return headers;
}

async function http<T>(path: string, init: RequestInit): Promise<T> {
	const clientInfo = rfStore.getRFState();

	const generatedHeaders = generateHeaders(path, init, clientInfo);

	let headers = new Headers({
		"Content-Type": "application/json",
		...generatedHeaders,
		...init.headers,
	});

	const config: RequestInit = {
		...init,
		headers: headers,
	};

	const response = await fetch(new Request(path, config));
	if (!response.ok) {
		const jsonValue = await response.json();

		if (typeof window !== "undefined" && NOT_NOTIFY_ERROR_MESSAGE_PATH.find((p) => path.includes(p)) === undefined) {
			notifyErrorMessage(jsonValue);
		}

		// 浏览器中 401 会被重定向到登录页，不需要再次处理
		// 中断所有后来的请求

		if (jsonValue.code === 401) {
			if (typeof window !== "undefined" && !isInApp()) {
				const locale = i18n?.language || "en";
				const redirect = window.location.href;
				const redirectPath = encodeURIComponent(redirect);
				const redirectUrl = `/${locale}/onboard/enter?redirect=${redirectPath}`;
				window.location.href = redirectUrl;
			}
		}

		log2Sentry(new ApiError(`http request error: code ${jsonValue.code} msg ${jsonValue.message}`), "error", jsonValue);

		return Promise.reject({
			code: jsonValue.code,
			message: jsonValue.message,
		});
	}

	// return response.json().catch(() => ({}));
	return JSONBig.parse(await response.text());
}

export async function get<T>(path: string, config?: RequestInit): Promise<T> {
	const init = { method: "get", ...config };
	return await http<T>(path, init);
}
export async function _delete<T>(path: string, config?: RequestInit): Promise<T> {
	const init = { method: "delete", ...config };
	return await http<T>(path, init);
}

export async function post<T, U>(path: string, body?: T, config?: RequestInit): Promise<U> {
	const init = { method: "post", body: JSON.stringify(body), ...config };
	return await http<U>(path, init);
}

export async function put<T, U>(path: string, body: T, config?: RequestInit): Promise<U> {
	const init = { method: "put", body: JSON.stringify(body), ...config };
	return await http<U>(path, init);
}

// Using `patch` is highly likely to result in a `405 Method Not Allowed`. `PATCH` is much more likely to succeed.
export async function patch<T, U>(path: string, body?: T, config?: RequestInit): Promise<U> {
	const init = { method: "PATCH", body: JSON.stringify(body), ...config };
	return await http<U>(path, init);
}
